import React, { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Send, Copy, Check, Plane } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";

const RELATIONS = ["Mother", "Father", "Spouse", "Sibling", "Child", "Grandparent", "Mother-in-Law", "Father-in-Law", "Close Friend", "Other"];

export default function InviteParent({ onDone }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", relation: "Mother", phone: "", city: "", age: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setErr("");
    if (!form.name.trim()) return setErr("Please add their name.");
    if (!form.phone.startsWith("+")) return setErr("Phone must include country code, e.g. +9198…");
    setBusy(true);
    try {
      const { data } = await api.post("/invites/parent", {
        name: form.name.trim(),
        relation: form.relation,
        phone: form.phone.trim(),
        city: form.city.trim() || null,
        age: form.age ? parseInt(form.age, 10) : null,
      });
      setResult(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not send invite.");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!result?.message) return;
    try {
      await navigator.clipboard.writeText(result.message);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) { /* clipboard unavailable */ }
  };

  if (result) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl border border-sand-200 p-5"
        data-testid="invite-success"
      >
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-full bg-sage-50 text-sage-700 flex items-center justify-center">
            <Plane size={16}/>
          </div>
          <p className="font-display text-xl text-ink-900">Invite sent!</p>
        </div>
        <p className="text-sm text-ink-700 mt-2">
          We&apos;ve sent a friendly SMS to <span className="font-semibold">{form.name.split(" ")[0]}</span> on{" "}
          <span className="font-semibold">{form.phone}</span>. They&apos;ve also been added to your Trusted Circle.
        </p>

        <div className="mt-4 bg-sand-50 rounded-2xl p-4 border border-sand-200">
          <p className="text-[11px] tracking-[0.16em] uppercase text-ink-500 font-semibold">SMS preview</p>
          <p className="text-[13px] text-ink-900 mt-2 leading-snug">{result.message}</p>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button onClick={copy} data-testid="invite-copy"
            className="h-12 rounded-full bg-ink-900 text-white font-semibold flex items-center justify-center gap-2 text-sm">
            {copied ? <><Check size={16}/> Copied</> : <><Copy size={16}/> Copy message</>}
          </button>
          <button onClick={() => { setResult(null); setForm({ name: "", relation: "Mother", phone: "", city: "", age: "" }); }}
            data-testid="invite-another"
            className="h-12 rounded-full bg-sage-500 text-white font-semibold text-sm">
            Invite another
          </button>
        </div>

        <button onClick={() => onDone && onDone()} data-testid="invite-back-circle"
          className="mt-3 w-full h-11 rounded-full bg-white border border-sand-200 text-ink-700 text-sm">
          Back to circle
        </button>
      </motion.div>
    );
  }

  return (
    <div className="space-y-4" data-testid="invite-parent-form">
      <div className="bg-terracotta-50 border border-terracotta-400/30 rounded-2xl p-4">
        <p className="text-[11px] tracking-[0.16em] uppercase text-terracotta-600 font-semibold">For families across borders</p>
        <p className="text-[13px] text-ink-900 mt-1 leading-snug">
          Set up Aarya for a loved one in 60 seconds. We&apos;ll send them a friendly SMS in your name with an install link.
        </p>
      </div>

      <div>
        <label className="text-[12px] tracking-wide uppercase text-ink-500">Their name</label>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Aarti Sharma"
          data-testid="invite-name"
          className="mt-1 w-full h-[56px] px-4 rounded-2xl border-2 border-sand-200 bg-white text-base outline-none focus:border-sage-500"/>
      </div>

      <div>
        <label className="text-[12px] tracking-wide uppercase text-ink-500">Relation</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {RELATIONS.map((r) => (
            <button key={r} onClick={() => setForm({ ...form, relation: r })}
              data-testid={`invite-relation-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`h-10 px-4 rounded-full text-sm border ${form.relation === r ? "bg-ink-900 text-white border-ink-900" : "bg-white text-ink-700 border-sand-200"}`}>
              {r}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[12px] tracking-wide uppercase text-ink-500">Their mobile (with country code)</label>
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="+919810000001" inputMode="tel"
          data-testid="invite-phone"
          className="mt-1 w-full h-[56px] px-4 rounded-2xl border-2 border-sand-200 bg-white text-base outline-none focus:border-sage-500"/>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[12px] tracking-wide uppercase text-ink-500">City <span className="lowercase text-ink-300">(opt)</span></label>
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
            placeholder="Lucknow"
            data-testid="invite-city"
            className="mt-1 w-full h-[56px] px-4 rounded-2xl border-2 border-sand-200 bg-white text-base outline-none focus:border-sage-500"/>
        </div>
        <div>
          <label className="text-[12px] tracking-wide uppercase text-ink-500">Age <span className="lowercase text-ink-300">(opt)</span></label>
          <input value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value.replace(/\D/g, "").slice(0, 3) })}
            placeholder="64" inputMode="numeric"
            data-testid="invite-age"
            className="mt-1 w-full h-[56px] px-4 rounded-2xl border-2 border-sand-200 bg-white text-base outline-none focus:border-sage-500"/>
        </div>
      </div>

      {err && <p className="text-sm text-brick-500" data-testid="invite-error">{err}</p>}

      <button onClick={submit} disabled={busy} data-testid="invite-submit"
        className="w-full h-[58px] rounded-full bg-sage-500 hover:bg-sage-600 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? "Sending…" : <><Send size={16}/> Send invite</>}
      </button>

      <p className="text-center text-[11px] text-ink-500 flex items-center justify-center gap-1">
        <Heart size={12} className="text-terracotta-500"/> Free for them too · A gift from IndoBlitz
      </p>
    </div>
  );
}
